import { useEffect, useRef, useCallback, forwardRef, useState } from "react";
import { useUnit } from "effector-react";
import { MathUtils } from "three";
import { $sceneBindings } from "../../state/appState";
import { $cameraConfig } from "../../state/appConfig";
import { $viewMode } from "../../state/viewMode";
import styles from "./CameraDebugOverlay.module.css";

const fmt = (n: number, digits = 3): string => n.toFixed(digits);
const deg = (rad: number): string => `${MathUtils.radToDeg(rad).toFixed(1)}°`;

export const CameraDebugOverlay = () => {
  const { sceneBindings, cameraConfig, viewMode } = useUnit({
    sceneBindings: $sceneBindings,
    cameraConfig: $cameraConfig,
    viewMode: $viewMode,
  });

  const posXRef = useRef<HTMLSpanElement>(null);
  const posYRef = useRef<HTMLSpanElement>(null);
  const posZRef = useRef<HTMLSpanElement>(null);
  const rotXRef = useRef<HTMLSpanElement>(null);
  const rotYRef = useRef<HTMLSpanElement>(null);
  const rotZRef = useRef<HTMLSpanElement>(null);
  const fovRef = useRef<HTMLSpanElement>(null);
  const aspectRef = useRef<HTMLSpanElement>(null);
  const nearFarRef = useRef<HTMLSpanElement>(null);
  const orbitTargetRef = useRef<HTMLSpanElement>(null);
  const orbitDistRef = useRef<HTMLSpanElement>(null);
  const heightRef = useRef<HTMLSpanElement>(null);
  const polarAngleRef = useRef<HTMLSpanElement>(null);
  const rafRef = useRef<number>(0);

  const tick = useCallback(() => {
    if (!sceneBindings) return;

    const { camera, controls } = sceneBindings;
    const orbit = controls.orbit;

    if (posXRef.current) posXRef.current.textContent = fmt(camera.position.x);
    if (posYRef.current) posYRef.current.textContent = fmt(camera.position.y);
    if (posZRef.current) posZRef.current.textContent = fmt(camera.position.z);

    if (rotXRef.current) rotXRef.current.textContent = deg(camera.rotation.x);
    if (rotYRef.current) rotYRef.current.textContent = deg(camera.rotation.y);
    if (rotZRef.current) rotZRef.current.textContent = deg(camera.rotation.z);

    if (fovRef.current) fovRef.current.textContent = fmt(camera.fov, 1);
    if (aspectRef.current) aspectRef.current.textContent = fmt(camera.aspect, 2);
    if (nearFarRef.current) nearFarRef.current.textContent = `${fmt(camera.near, 2)} / ${fmt(camera.far, 1)}`;

    const target = orbit.target;
    if (orbitTargetRef.current) {
      orbitTargetRef.current.textContent = `${fmt(target.x)}  ${fmt(target.y)}  ${fmt(target.z)}`;
    }
    if (orbitDistRef.current) {
      orbitDistRef.current.textContent = fmt(camera.position.distanceTo(target));
    }

    if (heightRef.current) {
      const distance = camera.position.distanceTo(target);
      const minH = orbit.clampMinHeight;
      const maxH = orbit.clampMaxHeight;
      const maxHFar = orbit.clampMaxHeightFar;
      let effectiveMaxH = maxH;
      if (maxH !== Infinity && maxHFar < maxH && orbit.maxDistance > orbit.minDistance) {
        const t = MathUtils.clamp(
          (distance - orbit.minDistance) / (orbit.maxDistance - orbit.minDistance),
          0,
          1,
        );
        effectiveMaxH = MathUtils.lerp(maxH, maxHFar, t);
      }
      heightRef.current.textContent = `${fmt(minH)} — ${fmt(effectiveMaxH)}`;
    }

    if (polarAngleRef.current) {
      const dx = camera.position.x - target.x;
      const dy = camera.position.y - target.y;
      const dz = camera.position.z - target.z;
      const r = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const polar = r > 0 ? Math.acos(MathUtils.clamp(dy / r, -1, 1)) : 0;
      polarAngleRef.current.textContent = deg(polar);
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [sceneBindings]);

  useEffect(() => {
    if (!sceneBindings) return;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [sceneBindings, tick]);

  const [copied, setCopied] = useState(false);

  const handleCopyPose = useCallback(() => {
    if (!sceneBindings) return;
    const { camera, controls } = sceneBindings;
    const pos = camera.position;
    const target = controls.orbit.target;
    const pose = {
      position: { x: +pos.x.toFixed(3), y: +pos.y.toFixed(3), z: +pos.z.toFixed(3) },
      target: { x: +target.x.toFixed(3), y: +target.y.toFixed(3), z: +target.z.toFixed(3) },
    };
    const text = JSON.stringify(pose, null, 2);
    console.log("Camera pose", pose);
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [sceneBindings]);

  if (!sceneBindings) return null;

  return (
    <div className={styles.root}>
      <div className={styles.heading}>Position</div>
      <Row label="X" ref={posXRef} />
      <Row label="Y" ref={posYRef} />
      <Row label="Z" ref={posZRef} />

      <div className={styles.heading}>Rotation</div>
      <Row label="X" ref={rotXRef} />
      <Row label="Y" ref={rotYRef} />
      <Row label="Z" ref={rotZRef} />

      <div className={styles.heading}>Projection</div>
      <Row label="FOV" ref={fovRef} />
      <Row label="Aspect" ref={aspectRef} />
      <Row label="Near / Far" ref={nearFarRef} />

      <div className={styles.heading}>Mode</div>
      <div className={styles.row}>
        <span className={styles.label}>View</span>
        <span className={styles.value}>{viewMode}</span>
      </div>

      {viewMode === "orbit" && (
        <>
          <div className={styles.heading}>Orbit</div>
          <Row label="Target" ref={orbitTargetRef} />
          <Row label="Distance" ref={orbitDistRef} />
          <Row label="Polar Now" ref={polarAngleRef} />
        </>
      )}

      <div className={styles.heading}>Constraints</div>
      <Row label="Height" ref={heightRef} />
      <div className={styles.row}>
        <span className={styles.label}>Orbit Dist</span>
        <span className={styles.value}>
          {fmt(cameraConfig.orbitMinDistance)} — {fmt(cameraConfig.orbitMaxDistance)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Polar</span>
        <span className={styles.value}>
          {deg(cameraConfig.orbitMinPolarAngle)} — {deg(cameraConfig.orbitMaxPolarAngle)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Vertical</span>
        <span className={styles.value}>
          {deg(cameraConfig.verticalMin)} — {deg(cameraConfig.verticalMax)}
        </span>
      </div>

      <button className={styles.copyButton} onClick={handleCopyPose}>
        {copied ? "Copied!" : "Copy Pose"}
      </button>
    </div>
  );
};

const Row = forwardRef<HTMLSpanElement, { label: string }>(
  ({ label }, ref) => (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value} ref={ref}>—</span>
    </div>
  )
);
Row.displayName = "Row";
